import { cva, type VariantProps } from 'class-variance-authority';
import { type ComponentProps } from 'react';

import { PHOEBE_MARK_PATH } from '@apps/web/components/ui/phoebe_mark';
import { cn } from '@apps/web/lib/utils';

const phoebeLoaderVariants = cva(
    'inline-block shrink-0 overflow-visible transition-colors',
    {
        variants: {
            size: {
                xs: 'size-3',
                sm: 'size-3.5',
                md: 'size-4',
                lg: 'size-5',
                xl: 'size-6',
            },
            state: {
                thinking: 'text-muted-foreground',
                complete: 'text-agent-action',
            },
        },
        defaultVariants: {
            size: 'md',
            state: 'thinking',
        },
    },
);

type PhoebeLoaderProps = Omit<ComponentProps<'svg'>, 'size'> &
    VariantProps<typeof phoebeLoaderVariants> & {
        readonly label?: string;
    };

/**
 * Runs a bright segment around the Phoebe mark while work is active, then
 * settles into the solid purple mark when its state changes to complete.
 * Reduced-motion users see the same two states without the moving segment.
 */
function PhoebeLoader({
    className,
    size,
    state = 'thinking',
    label,
    ...props
}: PhoebeLoaderProps) {
    return (
        <svg
            viewBox="35 35 108 108"
            xmlns="http://www.w3.org/2000/svg"
            role={label ? 'status' : 'presentation'}
            aria-label={label}
            aria-hidden={label ? undefined : 'true'}
            data-slot="phoebe-loader"
            data-state={state}
            className={cn(phoebeLoaderVariants({ size, state }), className)}
            {...props}
        >
            <g>
                <path
                    d={PHOEBE_MARK_PATH}
                    pathLength="1"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="phoebe-loader-guide"
                />
                <path
                    d={PHOEBE_MARK_PATH}
                    pathLength="1"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="phoebe-loader-comet"
                />
                <path
                    d={PHOEBE_MARK_PATH}
                    fill="currentColor"
                    className="phoebe-loader-fill"
                />
            </g>
        </svg>
    );
}

export { PhoebeLoader };
